import { get_user_country_or_detect } from "@/lib/location/country-detection";
import {
  is_challenge_restricted,
  is_platform_restricted,
} from "@/lib/location/restricted-countries";

let product_variants = [];
let filtered_variants = [];

/**
 * Filters product variants based on region restrictions
 * @param {Array<Object>} variants - Raw product variants to filter
 * @param {string} user_country - ISO country code
 * @returns {Array<Object>} Allowed variants for the country
 */
const filter_products_by_country = (variants, user_country) => {
  return variants.filter((variant) => {
    if (variant.account_size && variant.plan) {
      const restricted = is_challenge_restricted(
        user_country,
        variant.account_size,
        variant.plan
      );
      if (restricted) return false;
    }
    if (variant.platform) {
      const restricted = is_platform_restricted(user_country, variant.platform);
      if (restricted) return false;
    }
    return true;
  });
};

/**
 * Populates account size select with filtered variant values
 * @returns {void}
 */
const populate_account_sizes = () => {
  const account_size_select = document.getElementById(
    "modal-account-size-select"
  );
  if (!account_size_select || !filtered_variants.length) return;
  const account_sizes = [
    ...new Set(filtered_variants.map((variant) => variant.account_size)),
  ];
  const placeholder = account_size_select.querySelector(
    '[data-placeholder="true"]'
  );
  account_size_select.innerHTML = "";
  if (placeholder) account_size_select.appendChild(placeholder);
  account_sizes.forEach((size) => {
    const option = document.createElement("option");
    option.value = size;
    option.textContent = size;
    account_size_select.appendChild(option);
  });
};

/**
 * Updates plan options based on selected account size
 * @returns {void}
 */
const update_plan_options = () => {
  const account_size_select = document.getElementById(
    "modal-account-size-select"
  );
  const plan_select = document.getElementById("modal-plan-select");
  if (!account_size_select || !plan_select || !filtered_variants.length) return;
  const selected_account_size = account_size_select.value;
  const current_plan = plan_select.value;
  const available_plans = [
    ...new Set(
      filtered_variants
        .filter((variant) => variant.account_size === selected_account_size)
        .map((variant) => variant.plan)
    ),
  ];
  const placeholder = plan_select.querySelector('[data-placeholder="true"]');
  plan_select.innerHTML = "";
  if (placeholder) plan_select.appendChild(placeholder);
  available_plans.forEach((plan) => {
    const option = document.createElement("option");
    option.value = plan;
    option.textContent = plan;
    plan_select.appendChild(option);
  });
  if (current_plan && available_plans.includes(current_plan))
    plan_select.value = current_plan;
};

/**
 * Updates platform options based on selected account size and plan
 * @returns {void}
 */
const update_platform_options = () => {
  const account_size_select = document.getElementById(
    "modal-account-size-select"
  );
  const plan_select = document.getElementById("modal-plan-select");
  const platform_select = document.getElementById("modal-platform-select");
  if (
    !account_size_select ||
    !plan_select ||
    !platform_select ||
    !filtered_variants.length
  )
    return;
  const selected_account_size = account_size_select.value;
  const selected_plan = plan_select.value;
  const current_platform = platform_select.value;
  const available_platforms = [
    ...new Set(
      filtered_variants
        .filter(
          (variant) =>
            variant.account_size === selected_account_size &&
            variant.plan === selected_plan
        )
        .map((variant) => variant.platform)
    ),
  ];
  const placeholder = platform_select.querySelector(
    '[data-placeholder="true"]'
  );
  platform_select.innerHTML = "";
  if (placeholder) platform_select.appendChild(placeholder);
  available_platforms.forEach((platform) => {
    const option = document.createElement("option");
    option.value = String(platform);
    option.textContent = String(platform);
    platform_select.appendChild(option);
  });
  if (current_platform && available_platforms.includes(current_platform))
    platform_select.value = current_platform;
};

/**
 * Updates URL query parameters for variation and coupon
 * @param {{variation_id?: number|null, coupon?: string|null}} params - Values to set or clear
 * @returns {void}
 */
const update_url_parameters = (parameters) => {
  const url = new URL(window.location.href);
  if (Object.prototype.hasOwnProperty.call(parameters, "variation_id")) {
    const variation_id_value = parameters.variation_id;
    if (variation_id_value === null) url.searchParams.delete("variation_id");
    else if (variation_id_value)
      url.searchParams.set("variation_id", String(variation_id_value));
  }
  if (Object.prototype.hasOwnProperty.call(parameters, "coupon")) {
    const coupon_value = parameters.coupon;
    if (coupon_value === null) url.searchParams.delete("coupon");
    else if (coupon_value) url.searchParams.set("coupon", coupon_value);
  }
  window.history.replaceState({}, "", url.toString());
};

/**
 * Finds variant matching the current modal selections
 * @returns {Object|null} Matching variant or null
 */
const find_matching_variant = () => {
  const account_size_select = document.getElementById(
    "modal-account-size-select"
  );
  const plan_select = document.getElementById("modal-plan-select");
  const platform_select = document.getElementById("modal-platform-select");
  if (!account_size_select || !plan_select || !platform_select) return null;
  return (
    filtered_variants.find(
      (variant) =>
        variant.account_size === account_size_select.value &&
        variant.plan === plan_select.value &&
        variant.platform === platform_select.value
    ) || null
  );
};

/**
 * Preselects modal fields from variation_id in URL
 * @returns {void}
 */
const preselect_variant_from_url = () => {
  const url_parameters = new URLSearchParams(window.location.search);
  const variation_id = url_parameters.get("variation_id");
  if (!variation_id) return;
  const target_variant = filtered_variants.find(
    (variant) => String(variant.variant_id) === String(variation_id)
  );
  if (!target_variant) return;
  const account_size_select = document.getElementById(
    "modal-account-size-select"
  );
  const plan_select = document.getElementById("modal-plan-select");
  const platform_select = document.getElementById("modal-platform-select");
  if (account_size_select)
    account_size_select.value = target_variant.account_size;
  if (plan_select) plan_select.value = target_variant.plan;
  if (platform_select) platform_select.value = target_variant.platform;
  update_plan_options();
  update_platform_options();
};

/**
 * Wires select change events to update options and URL
 * @returns {void}
 */
const setup_event_listeners = () => {
  const account_size_select = document.getElementById(
    "modal-account-size-select"
  );
  const plan_select = document.getElementById("modal-plan-select");
  const platform_select = document.getElementById("modal-platform-select");
  if (!account_size_select || !plan_select || !platform_select) return;
  account_size_select.addEventListener("change", () => {
    update_plan_options();
    update_platform_options();
    const variant = find_matching_variant();
    update_url_parameters({
      variation_id: variant ? variant.variant_id : null,
    });
  });
  plan_select.addEventListener("change", () => {
    update_platform_options();
    const variant = find_matching_variant();
    update_url_parameters({
      variation_id: variant ? variant.variant_id : null,
    });
  });
  platform_select.addEventListener("change", () => {
    const variant = find_matching_variant();
    update_url_parameters({
      variation_id: variant ? variant.variant_id : null,
    });
  });
};

/**
 * Dispatches a custom selection event
 * @param {string} event_type - Event name to dispatch
 * @param {Object} data - Event detail payload
 * @returns {void}
 */
const dispatch_selection_event = (event_type, data) => {
  const custom_event = new CustomEvent(event_type, {
    detail: data,
    bubbles: true,
  });
  document.dispatchEvent(custom_event);
};

/**
 * Wires modal cancel/confirm buttons and updates URL
 * @returns {void}
 */
const setup_button_event_listeners = () => {
  const region_modal = document.getElementById("region-modal");
  const cancel_button = document.getElementById("cancel-button");
  const confirm_button = document.getElementById("confirm-button");
  cancel_button?.addEventListener("click", () => {
    update_url_parameters({ variation_id: null });
    region_modal?.classList.remove("flex");
    region_modal?.classList.add("hidden");
  });
  confirm_button?.addEventListener("click", () => {
    dispatch_selection_event("region-modal-confirm", {});
    region_modal?.classList.remove("flex");
    region_modal?.classList.add("hidden");
  });
};

/**
 * Initializes modal with filtered products and listeners
 * @returns {Promise<void>}
 */
export const initialize_modal_with_filtered_products = async () => {
  try {
    const variants_data_element = document.querySelector("[data-variants]");
    if (!variants_data_element) return;
    product_variants = JSON.parse(
      variants_data_element.dataset.variants || "[]"
    );
    const user_country = await get_user_country_or_detect();
    filtered_variants = user_country
      ? filter_products_by_country(product_variants, user_country)
      : product_variants;
    populate_account_sizes();

    const modal = document.getElementById("region-modal");
    if (modal && !modal.internal_listeners_attached) {
      setup_event_listeners();
      setup_button_event_listeners();
      modal.internal_listeners_attached = true;
    }

    preselect_variant_from_url();
  } catch (error) {
    console.error("Error initializing modal with filtered products:", error);
  }
};

/**
 * Initializes region modal listeners for lazy setup on modal show
 * @returns {void}
 */
export const init_region_modal_listeners = () => {
  const modal = document.getElementById("region-modal");
  if (!modal || modal.dataset.initListenersAttached) return;
  modal.addEventListener("modal-shown", async () => {
    await initialize_modal_with_filtered_products();
  });
  modal.dataset.initListenersAttached = "true";
};
